import { FC, useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Autocomplete, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import { UserFormProps, FormValues } from './interfaces/IUserForm';
import { EmailController, SelectController, TextController } from '../utils/controllers';
import { BaseForm } from '../utils/BaseForm';
import { useHUser } from '../items/hooks/HUsers';
import { useHCompanies } from '../items/hooks/HCompanies';

/**
 * UserForm dialog for adding/editing users.
 * Handles form state, validation, and submission.
 *
 * @param {UserFormProps} props - The props for the component.
 * @returns {JSX.Element} The rendered UserForm dialog.
 */
const UserForm: FC<UserFormProps> = ({ open, onClose, onSubmit, title = 'Add User', submitText = 'Submit', initialValues }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { roles, loading: rolesLoading, fetchRoles } = useHUser();
    const { companies, loading: companiesLoading } = useHCompanies();

    const companyOptions = companies
        .map((company) => ({
            id: String(company.id ?? company.company_id ?? ''),
            name: String(company.company_name ?? company.company ?? company.name ?? '').trim(),
        }))
        .filter((company) => company.id && company.name);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    const defaultValues: FormValues = {
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        companyId: '',
        role_id: 0n,
        role: '',
        ...initialValues,
    };

    /**
     * Transform form values to API payload format.
     * @param {FormValues} data - The form values.
     * @returns {object} The transformed payload.
     */
    const transformData = (data: FormValues) => {
        const selectedCompany = companyOptions.find((company) => company.name === data.company.trim());

        return {
            firstName: data.firstName.trim(),
            lastName: data.lastName.trim(),
            email: data.email.trim(),
            company: data.company.trim(),
            companyId: selectedCompany?.id || '',
            role_id: data.role_id,
            roleId: data.role_id,
            role: data.role.trim(),
        };
    };

    /**
     * Handle form submission and call onSubmit prop.
     * @param {object} transformedData - The transformed payload.
     */
    const handleSubmit = async (transformedData: any) => {
        console.log('[UserForm] handleSubmit called with transformedData:', transformedData);
        setIsSubmitting(true);
        try {
            await onSubmit(transformedData);
        } catch (error) {
            // Error is already handled by BaseForm
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <BaseForm
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            title={title}
            defaultValues={defaultValues}
            transformData={transformData}
            customActions={(formState, handleSubmitClick, handleCancel) => (
                <>
                    <Button onClick={() => { console.debug('[UserForm] Cancel clicked'); handleCancel(); }} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => { console.debug('[UserForm] Submit clicked'); handleSubmitClick(); }}
                        variant='contained'
                        color='primary'
                        disabled={!formState.isValid || isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : submitText}
                    </Button>
                </>
            )}
            loading={isSubmitting}
            submitText={submitText}
        >
            {(control) => (
                <>
                    <TextController
                        name='firstName'
                        control={control}
                        label='First Name'
                        rules={{ required: 'First name is required' }}
                        sx={{ marginBottom: 2 }}
                    />
                    <TextController
                        name='lastName'
                        control={control}
                        label='Last Name'
                        rules={{ required: 'Last name is required' }}
                        sx={{ marginBottom: 2 }}
                    />
                    <EmailController
                        name='email'
                        control={control}
                        label='Email'
                        rules={{ required: 'Email is required' }}
                        sx={{ marginBottom: 2 }}
                    />
                    <Controller
                        name='company'
                        control={control}
                        rules={{
                            required: 'Company is required',
                            validate: (value) =>
                                companyOptions.some((company) => company.name === String(value).trim()) ||
                                'Select one of the suggested companies',
                        }}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                options={companyOptions}
                                loading={companiesLoading}
                                freeSolo={false}
                                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={companyOptions.find((option) => option.name === field.value) ?? null}
                                onChange={(_, selectedOption) => {
                                    if (!selectedOption) {
                                        field.onChange('');
                                        return;
                                    }

                                    field.onChange(selectedOption.name);
                                }}
                                onInputChange={(_, inputValue, reason) => {
                                    if (reason === 'input') {
                                        field.onChange(inputValue);
                                    }
                                    if (reason === 'clear') {
                                        field.onChange('');
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Company'
                                        margin='dense'
                                        fullWidth
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message || 'Type to search and select one company'}
                                        sx={{ marginBottom: 2 }}
                                    />
                                )}
                            />
                        )}
                    />
                    <SelectController
                        name='role'
                        control={control}
                        label='Role'
                        options={roles.map((role) => ({ value: role.role, label: role.role }))}
                        rules={{ required: 'Role is required' }}
                        disabled={rolesLoading}
                    />
                </>
            )}
        </BaseForm>
    );
};

export default UserForm;
